import React, { useState, useRef, useEffect } from 'react';
import { Modal, Input, List, Button, Typography, Space, Spin, Empty } from 'antd';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { searchCompanies } from '../../../service/authService';
import { useImpersonation } from '../../../contexts/impersonation-context/ImpersonationContext';
import styled from 'styled-components';
import { colors } from '../../../styles/theme';
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface Company {
  id: number;
  name: string;
  email?: string;
  city?: string;
  isActive?: boolean;
}

interface ImpersonationModalProps {
  open: boolean;
  onClose: () => void;
}

const SearchInput = styled(Input)`
  .ant-input {
    font-size: 16px;
  }
`;

const CompanyListItem = styled(List.Item)`
  cursor: pointer;
  padding: 16px;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.backgroundHover};
  }

  &.selected {
    background-color: ${colors.primaryLight};
    border: 2px solid ${colors.primary};
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompanyName = styled(Text)`
  font-weight: 500;
  font-size: 15px;
`;

const CompanyMeta = styled(Text)`
  font-size: 13px;
  color: ${colors.textSecondary};
`;

export const ImpersonationModal: React.FC<ImpersonationModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { startImpersonation, isImpersonating, stopImpersonation } = useImpersonation();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Search function
  const performSearch = async (query: string) => {
    if (!query || query.trim().length === 0) {
      setCompanies([]);
      return;
    }

    setLoading(true);
    try {
      const result = await searchCompanies(query);
      setCompanies(result.companies || []);
    } catch (error) {
      console.error('Search error:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedCompany(null); // Clear selection on new search

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer (300ms debounce)
    debounceTimer.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
  };

  const handleConfirm = () => {
    if (selectedCompany) {
      // Show frozen overlay
      setIsSwitching(true);

      // Call startImpersonation which sets cookies AND state
      startImpersonation(selectedCompany);

      // Give the app time to reload data (branches, company info, etc.)
      setTimeout(() => {
        setIsSwitching(false);
        onClose();
      }, 1500);
    }
  };

  const handleCancel = () => {
    setSearchQuery('');
    setCompanies([]);
    setSelectedCompany(null);
    onClose();
  };

  return (
    <Modal
      title={t("impersonationModal.title")}
      open={open}
      onCancel={handleCancel}
      maskClosable={!isSwitching}
      closable={!isSwitching}
      footer={[
        isImpersonating && (
          <Button
            key="back-to-dev"
            danger
            onClick={() => {
              setIsSwitching(true);
              stopImpersonation();
              // stopImpersonation already redirects after 300ms, no need to close modal
            }}
            style={{ marginRight: 'auto' }}
            disabled={isSwitching}
          >
            {t("impersonationModal.backToDevPanel")}
          </Button>
        ),
        <Button key="cancel" onClick={handleCancel} disabled={isSwitching}>
          {t("impersonationModal.cancel")}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!selectedCompany}
          loading={isSwitching}
        >
          {t("impersonationModal.confirm")}
        </Button>,
      ]}
      width={600}
    >
      {/* Frozen overlay during company switch */}
      {isSwitching && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 9999,
            pointerEvents: 'all',
            cursor: 'wait',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16, color: '#666' }}>
              {t("impersonationModal.switchingToAccount", { name: selectedCompany?.name })}
            </div>
          </div>
        </div>
      )}

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {t("impersonationModal.searchHint")}
          </Text>
          <SearchInput
            placeholder={t("impersonationModal.searchPlaceholder")}
            prefix={<MagnifyingGlass size={20} />}
            value={searchQuery}
            onChange={handleSearchChange}
            size="large"
            autoFocus
          />
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!loading && searchQuery && companies.length === 0 && (
          <Empty
            description={t("impersonationModal.noCompaniesFound")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {!loading && companies.length > 0 && (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <List
              dataSource={companies}
              renderItem={(company) => (
                <CompanyListItem
                  onClick={() => handleCompanySelect(company)}
                  className={selectedCompany?.id === company.id ? 'selected' : ''}
                >
                  <CompanyInfo>
                    <CompanyName>
                      {company.name} <Text type="secondary">(ID: {company.id})</Text>
                    </CompanyName>
                    {company.email && (
                      <CompanyMeta>
                        {t("impersonationModal.emailLabel", { email: company.email })}
                      </CompanyMeta>
                    )}
                    {company.city && (
                      <CompanyMeta>
                        {t("impersonationModal.cityLabel", { city: company.city })}
                      </CompanyMeta>
                    )}
                    {company.isActive !== undefined && (
                      <CompanyMeta>
                        {t("impersonationModal.statusLabel", { status: company.isActive ? t("impersonationModal.statusActive") : t("impersonationModal.statusInactive") })}
                      </CompanyMeta>
                    )}
                  </CompanyInfo>
                </CompanyListItem>
              )}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};
